// ** React Imports
import { createContext, useState, ReactNode, useEffect, useMemo } from "react";
import * as LDClient from "launchdarkly-js-client-sdk";
import fallbackFlags from "utils/shared/flags/fallback-flags";

interface LaunchDarklyProviderProps {
  children: ReactNode;
  user: any;
}

export interface LaunchDarklyState {
  flags: LDClient.LDFlagSet;
  client: LDClient.LDClient | null;
  flagsInitialized: boolean;
}

const launchDarklyDefault: LaunchDarklyState = {
  flags: {},
  client: null,
  flagsInitialized: false,
};

// ** Create Context
export const LaunchDarklyContext = createContext<LaunchDarklyState>(launchDarklyDefault);

export const LaunchDarklyStore = ({ children, user }: LaunchDarklyProviderProps) => {
  // ** State
  const [client, setClient] = useState<LDClient.LDClient | null>(null);
  const [flags, setFlags] = useState<LDClient.LDFlagSet>({});
  const [flagsInitialized, setFlagsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!user?.id) return;

    const context: LDClient.LDContext = {
      kind: "user",
      key: user.email,
    };

    const clientSideId = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID;

    if (!clientSideId) {
      console.error("No Launch Darkly Client ID in env");
      return;
    }

    const ldClient = LDClient.initialize(clientSideId, context);

    ldClient
      .waitForInitialization()
      .then(() => {
        setClient(ldClient);

        const allFlags = ldClient.allFlags();

        // combine all launch darkly flags with all flags within our config so even if it is removed in launch darkly - we have a backup to prevent losing features
        const allFlagsWithFallbackFlags: LDClient.LDFlagSet = Object.keys(fallbackFlags).reduce(
          (accumulated, fallbackFlagKey) => {
            if (!accumulated.hasOwnProperty(fallbackFlagKey)) {
              accumulated[fallbackFlagKey] = fallbackFlags[fallbackFlagKey];
            }

            return accumulated;
          },
          { ...allFlags },
        );

        setFlags(allFlagsWithFallbackFlags);
        setFlagsInitialized(true);

        ldClient.on("change", (settings) => {
          const updatedFlags = ldClient.allFlags();

          // combine all launch darkly flags with all flags within our config so even if it is removed in launch darkly - we have a backup to prevent losing features
          const allUpdatedFlagsWithFallbackFlags: LDClient.LDFlagSet = Object.keys(fallbackFlags).reduce(
            (accumulated, fallbackFlagKey) => {
              if (!accumulated.hasOwnProperty(fallbackFlagKey)) {
                accumulated[fallbackFlagKey] = fallbackFlags[fallbackFlagKey];
              }

              return accumulated;
            },
            { ...updatedFlags },
          );

          setFlags(allUpdatedFlagsWithFallbackFlags);
        });
      })
      .catch((error) => {
        console.error("Failed to initialize Launch Darkly flags", error);

        // in case we are unable to reach launch darkly - use fallback flag values to ensure we don't lose features
        setFlags(fallbackFlags);
        setFlagsInitialized(true);
      });

    return () => {
      ldClient.close();
      setClient(null);
    };
  }, [user.id]);

  const values = {
    flags,
    client,
    flagsInitialized,
  };

  return <LaunchDarklyContext.Provider value={values}>{children}</LaunchDarklyContext.Provider>;
};

export const LaunchDarklyConsumer = LaunchDarklyContext.Consumer;
